// Stripe.js injects a floating "easel" iframe (Stripe Link sign-up prompt)
// at document.body level whenever a PaymentElement mounts. Two problems:
//
//   1. It uses `style="display: block !important;"` inline, which beats any
//      `!important` CSS rule we could author from a stylesheet (inline
//      important wins the cascade against external important at equal
//      specificity).
//   2. Stripe.js doesn't always remove it on Elements unmount, so the badge
//      keeps floating in the bottom-right corner of the PWA on later screens
//      and covers our nav icons (My Clips / Profile). Client-reported
//      across browsers post-onboarding.
//
// We don't need the Link sign-up prompt — the SetupIntent is created
// server-side with `payment_method_types: ['card']` (Link is not a card-
// adjacent feature we expose). Hide the iframe whenever it appears by
// overriding its inline style. Safer/cleaner alternatives:
//
//   - Disable Stripe Link in the Stripe Dashboard (one-click, recommended)
//   - Set `paymentMethodOrder: ['card']` on PaymentElement (limited effect)
//
// SMRTSTATS-297 follow-up.

const HIDE_PATTERNS = [
  'elements-inner-easel',
  'universal-link-modal-inner',
];

const shouldHide = (iframe) => {
  const src = iframe?.src;
  if (!src) return false;
  if (!src.includes('js.stripe.com')) return false;
  return HIDE_PATTERNS.some((p) => src.includes(p));
};

const hide = (iframe) => {
  if (!shouldHide(iframe)) return;
  // Use the third-arg priority form so we beat Stripe's inline `!important`.
  iframe.style.setProperty('display', 'none', 'important');
  iframe.style.setProperty('visibility', 'hidden', 'important');
  iframe.style.setProperty('pointer-events', 'none', 'important');
};

let observer = null;

export const startHidingStripeLinkUi = () => {
  if (typeof window === 'undefined' || observer) return;
  // Initial sweep for any iframes already attached at boot.
  document.querySelectorAll('iframe').forEach(hide);
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (!node || node.nodeType !== 1) return;
        if (node.tagName === 'IFRAME') {
          hide(node);
          return;
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('iframe').forEach(hide);
        }
      });
      // Stripe also mutates the inline `style` attribute mid-lifecycle —
      // re-apply our override if any target iframe is touched.
      if (m.type === 'attributes' && m.target?.tagName === 'IFRAME') {
        hide(m.target);
      }
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'src'],
  });
};

export const stopHidingStripeLinkUi = () => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
};
